/**
 * UpsideMLDashboard v3 — Completely redesigned ML Upside Screener
 *
 * Major layout changes:
 * - Hero header with gradient + live market pulse gauge
 * - Smart Cards row with glassmorphism effect
 * - Strong Buy "Action Strip" — most important info up front
 * - Single-page with collapsible sections (no tabs)
 * - Inline tier filters + sortable table
 * - Dense heatmap tiles with hover cards
 * - Responsive: mobile-first
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Typography, CircularProgress, Alert, Paper, Skeleton,
  Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  TextField, InputAdornment, Tooltip, Select, MenuItem, FormControl,
  Collapse, Fade, Divider, TableSortLabel, Badge, Snackbar,
  Dialog, DialogContent, Button, Tab, Tabs,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  TrendingUp as TrendingUpIcon,
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Info as InfoIcon,
  EmojiEvents as TrophyIcon,
  LocalFireDepartment as FireIcon,
  Warning as WarningIcon,
  TrendingDown as TrendingDownIcon,
  TrendingFlat as TrendingFlatIcon,
  Bolt as BoltIcon,
  ShowChart as ChartIcon,
  ExpandMore as ExpandMoreIcon,
  Speed as SpeedIcon,
  WbSunny as SunnyIcon,
  Cloud as CloudIcon,
  Calculate as CalculateIcon,
} from '@mui/icons-material';
import {
  ComposedChart, Area, Line, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer, Legend, ReferenceLine,
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { upsideMLAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS
   ═══════════════════════════════════════════════════════════════ */
const TIER = {
  3: { bg: '#dcfce7', bgAlt: '#f0fdf4', border: '#16a34a', text: '#15803d', label: 'HOT', chipBg: '#16a34a', chipColor: '#fff', gradient: 'linear-gradient(135deg, #16a34a, #22c55e)' },
  2: { bg: '#fef9c3', bgAlt: '#fefce8', border: '#ca8a04', text: '#a16207', label: 'WARM', chipBg: '#ca8a04', chipColor: '#fff', gradient: 'linear-gradient(135deg, #ca8a04, #eab308)' },
  1: { bg: '#dbeafe', bgAlt: '#eff6ff', border: '#2563eb', text: '#1d4ed8', label: 'MILD', chipBg: '#2563eb', chipColor: '#fff', gradient: 'linear-gradient(135deg, #2563eb, #3b82f6)' },
  0: { bg: '#f3f4f6', bgAlt: '#f9fafb', border: '#9ca3af', text: '#6b7280', label: 'COOL', chipBg: '#9ca3af', chipColor: '#fff', gradient: 'linear-gradient(135deg, #9ca3af, #d1d5db)' },
};

const RISK = {
  3: { bg: '#dc2626', color: '#fff', label: 'CRITICAL' },
  2: { bg: '#ea580c', color: '#fff', label: 'SEVERE' },
  1: { bg: '#d97706', color: '#fff', label: 'CAUTION' },
  0: { bg: '#e5e7eb', color: '#6b7280', label: 'SAFE' },
};

/* ═══════════════════════════════════════════════════════════════
   ANIMATED GAUGE — Donut-style market pulse
   ═══════════════════════════════════════════════════════════════ */
/* ═══════════════════════════════════════════════════════════════
   GLASS STAT CARD — Modern glassmorphism card
   ═══════════════════════════════════════════════════════════════ */
const GlassCard = ({ title, value, sub, color = '#16a34a', icon, delta, onClick }) => (
  <Paper
    elevation={0}
    onClick={onClick}
    sx={{
      px: 2, py: 2, borderRadius: 1.5,
      border: '1px solid #e5e7eb',
      bgcolor: '#fff',
      height: '100%',
      display: 'flex', flexDirection: 'column', justifyContent: 'center',
      position: 'relative',
      overflow: 'hidden',
      cursor: onClick ? 'pointer' : 'default',
      transition: 'transform 0.2s, box-shadow 0.2s',
      '&:hover': onClick ? {
        transform: 'translateY(-2px)',
        boxShadow: `0 4px 12px -2px rgba(0,0,0,0.08)`,
      } : {},
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0, left: 0, right: 0, height: 2.5,
        background: `linear-gradient(90deg, ${color}, ${color}88)`,
        borderRadius: '4px 4px 0 0',
      },
    }}
  >
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.8 }}>
      <Box sx={{
        width: 24, height: 24, borderRadius: 1,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        background: `${color}10`,
      }}>
        {icon}
      </Box>
      <Typography sx={{ fontSize: 11, fontWeight: 700, color: '#6b7280', letterSpacing: 0.3, textTransform: 'uppercase' }}>
        {title}
      </Typography>
    </Box>
    <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.8, mb: 0.5 }}>
      <Typography sx={{ fontSize: 26, fontWeight: 800, color, lineHeight: 1.2 }}>
        {value}
      </Typography>
      {delta !== undefined && delta !== null && (
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.2,
          px: 0.6, py: 0.15, borderRadius: 0.8,
          bgcolor: delta > 0 ? '#dcfce7' : delta < 0 ? '#fef2f2' : '#f3f4f6',
        }}>
          {delta > 0 ? <TrendingUpIcon sx={{ fontSize: 12, color: '#16a34a' }} /> :
            delta < 0 ? <TrendingDownIcon sx={{ fontSize: 12, color: '#dc2626' }} /> :
              <TrendingFlatIcon sx={{ fontSize: 12, color: '#9ca3af' }} />}
          <Typography sx={{
            fontSize: 11, fontWeight: 700,
            color: delta > 0 ? '#16a34a' : delta < 0 ? '#dc2626' : '#9ca3af',
          }}>
            {delta > 0 ? '+' : ''}{delta}
          </Typography>
        </Box>
      )}
    </Box>
    {sub && (
      <Typography sx={{ fontSize: 11.5, color: '#9ca3af', lineHeight: 1.4 }}>
        {sub}
      </Typography>
    )}
  </Paper>
);

/* ═══════════════════════════════════════════════════════════════
   COLLAPSIBLE SECTION — Smart container
   ═══════════════════════════════════════════════════════════════ */
const Section = ({ title, icon, children, action, defaultOpen = true, count, badge, sx: sxProp, contentSx }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 1.5,
        border: '1px solid #e5e7eb',
        overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        display: 'flex', flexDirection: 'column',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
        ...sxProp,
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.8, cursor: 'pointer',
          background: open ? 'linear-gradient(135deg, #fafafa 0%, #fff 100%)' : '#fafafa',
          borderBottom: open ? '1px solid #f3f4f6' : 'none',
          transition: 'all 0.2s',
          '&:hover': { background: '#f9fafb' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && <Box sx={{ color: '#9ca3af', display: 'flex' }}>{icon}</Box>}
          <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
            {title}
          </Typography>
          {count !== undefined && (
            <Chip label={count} size="small" sx={{
              height: 20, fontSize: 11, fontWeight: 700,
              bgcolor: '#f3f4f6', color: '#6b7280',
            }} />
          )}
          {badge && (
            <Chip label={badge} size="small" sx={{
              height: 20, fontSize: 10, fontWeight: 700,
              bgcolor: '#dcfce7', color: '#16a34a',
            }} />
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {action && <Box onClick={e => e.stopPropagation()}>{action}</Box>}
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: '#f3f4f6', transition: 'transform 0.3s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            <ExpandMoreIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
          </Box>
        </Box>
      </Box>
      <Collapse in={open} timeout={300}>
        <Box sx={{ p: 2.5, display: 'flex', flexDirection: 'column', ...contentSx }}>
          {children}
        </Box>
      </Collapse>
    </Paper>
  );
};

/* ═══════════════════════════════════════════════════════════════
   STOCK DETAIL DIALOG — Why is this a good opportunity?
   ═══════════════════════════════════════════════════════════════ */
const StockDetailDialog = ({ stock, open, onClose, t }) => {
  if (!stock) return null;
  const prob = (stock.ml_prob * 100).toFixed(1);
  const tier = stock.opportunity_tier || 0;
  const style = TIER[tier];
  const reasons = stock.reasons || [];
  const ind = stock.indicators || {};
  const pos = (stock.target_position * 100).toFixed(0);

  // Action & risk — same logic as table
  let actionLabel, actionColor, actionBg;
  if (stock.risk_tier >= 3) {
    actionLabel = t('upsideML.actions.avoid'); actionColor = '#7f1d1d'; actionBg = '#fef2f2';
  } else if (stock.risk_tier >= 2) {
    actionLabel = t('upsideML.actions.conflictWarn'); actionColor = '#dc2626'; actionBg = '#fef2f2';
  } else if (stock.buy_signal) {
    actionLabel = t('upsideML.actions.strongBuy'); actionColor = '#fff'; actionBg = '#16a34a';
  } else if (tier >= 3) {
    actionLabel = t('upsideML.actions.buy'); actionColor = '#fff'; actionBg = '#22c55e';
  } else if (tier >= 2) {
    actionLabel = t('upsideML.actions.accumulate'); actionColor = '#fff'; actionBg = '#ca8a04';
  } else if (tier >= 1) {
    actionLabel = t('upsideML.actions.watch'); actionColor = '#fff'; actionBg = '#2563eb';
  } else {
    actionLabel = t('upsideML.actions.skip'); actionColor = '#6b7280'; actionBg = '#f3f4f6';
  }

  const riskMeta = RISK[stock.risk_tier] || RISK[0];

  // Helper: gauge indicator row with visual bar + interpretation
  const IndGauge = ({ label, value, min, max, thresholds, invert }) => {
    // thresholds: [bad_below, good_above] — values between are neutral
    // invert: true means lower is better (e.g. DD from Peak, Support Prox)
    if (value == null) return null;
    const [badBelow, goodAbove] = thresholds;
    const isBad = invert ? value > goodAbove : value < badBelow;
    const isGood = invert ? value < badBelow : value > goodAbove;
    const sentimentLabel = isGood ? 'Tích cực' : isBad ? 'Tiêu cực' : 'Trung bình';
    const sentimentColor = isGood ? '#16a34a' : isBad ? '#dc2626' : '#9ca3af';
    const barColor = isGood ? '#22c55e' : isBad ? '#ef4444' : '#d1d5db';
    // Normalize value to 0-100 for the bar
    const clamped = Math.min(Math.max(value, min), max);
    const pct = ((clamped - min) / (max - min)) * 100;
    return (
      <Box sx={{ py: 0.7, px: 0.8 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.3 }}>
          <Typography sx={{ fontSize: 11, color: '#6b7280', fontWeight: 500 }}>{label}</Typography>
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: sentimentColor }}>{sentimentLabel}</Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
          <Box sx={{ flex: 1, height: 4, bgcolor: '#f3f4f6', borderRadius: 2, overflow: 'hidden' }}>
            <Box sx={{
              height: '100%', borderRadius: 2,
              width: `${pct}%`,
              bgcolor: barColor,
              transition: 'width 0.4s ease',
            }} />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth
      PaperProps={{ sx: { borderRadius: 1.5, overflow: 'hidden' } }}
    >
      {/* ──── Header ──── */}
      <Box sx={{
        background: style.gradient, px: 2, pt: 1.5, pb: 1.2,
        display: 'flex', alignItems: 'center', gap: 1.5,
        cursor: 'pointer',
      }} onClick={onClose}>
        {/* Left: Ticker + meta */}
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6 }}>
            <Typography sx={{ fontWeight: 800, fontSize: 20, color: '#fff', letterSpacing: 0.3 }}>
              {stock.ticker}
            </Typography>
            <Typography sx={{
              fontSize: 9, fontWeight: 800, letterSpacing: 0.5,
              bgcolor: actionBg, color: actionColor,
              borderRadius: 0.5, px: 0.6, py: 0.1, lineHeight: 1.4,
            }}>
              {actionLabel}
            </Typography>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.3,
              bgcolor: 'rgba(255,255,255,0.15)', borderRadius: 0.5, px: 0.5, py: 0.1,
            }}>
              <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: riskMeta.bg === '#e5e7eb' ? 'rgba(255,255,255,0.5)' : riskMeta.bg }} />
              <Typography sx={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{riskMeta.label}</Typography>
            </Box>
          </Box>
          <Typography sx={{ fontSize: 11, color: 'rgba(255,255,255,0.55)', mt: 0.2 }}>
            {stock.close != null ? Number(stock.close).toLocaleString() : ''} · {style.label} · {t('upsideML.tableHeaders.position')} {pos}%
          </Typography>
        </Box>

        {/* Right: Prob */}
        <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#fff', lineHeight: 1, flexShrink: 0 }}>
          {prob}%
        </Typography>
      </Box>

      <DialogContent sx={{ px: 2, pt: 1.5, pb: 1.5 }}>
        {/* ──── Signals ──── */}
        {reasons.length > 0 && (
          <Box sx={{ mb: 2 }}>
            <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, mb: 0.8 }}>
              {t('upsideML.tableHeaders.signals', 'Signals')}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6 }}>
              {reasons.map(r => (
                <Chip key={r} label={t(`upsideML.reasons.${r}`, r)} size="small" sx={{
                  height: 24, fontSize: 11, fontWeight: 600,
                  bgcolor: `${style.text}0D`, color: style.text,
                  border: `1px solid ${style.text}20`,
                }} />
              ))}
            </Box>
          </Box>
        )}

        {/* ──── Indicators (visual gauges) ──── */}
        <Box sx={{ bgcolor: '#f9fafb', borderRadius: 1, border: '1px solid #e5e7eb', overflow: 'hidden' }}>
          {/* Momentum */}
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, px: 0.8, pt: 0.8, pb: 0.2 }}>
            Động lượng
          </Typography>
          <IndGauge label="RSI" value={ind.rsi14} min={0} max={100} thresholds={[30, 55]} />
          <IndGauge label="Biên BB" value={ind.bb_pos} min={0} max={1} thresholds={[0.2, 0.5]} />
          <IndGauge label="MACD" value={ind.macd_hist} min={-500} max={500} thresholds={[-50, 50]} />
          <IndGauge label="Khối lượng" value={ind.vol_z} min={-3} max={5} thresholds={[-0.5, 1]} />

          <Divider sx={{ my: 0.3 }} />

          {/* Returns */}
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, px: 0.8, pt: 0.5, pb: 0.2 }}>
            Hiệu suất
          </Typography>
          <IndGauge label="Lợi nhuận 5 phiên" value={ind.ret5 != null ? ind.ret5 * 100 : null} min={-15} max={15} thresholds={[-2, 2]} />
          <IndGauge label="Lợi nhuận 20 phiên" value={ind.ret20 != null ? ind.ret20 * 100 : null} min={-30} max={30} thresholds={[-5, 5]} />
          <IndGauge label="Sức mạnh vs VNI" value={ind.rs_vs_mkt_5 != null ? ind.rs_vs_mkt_5 * 100 : null} min={-10} max={10} thresholds={[-2, 2]} />

          <Divider sx={{ my: 0.3 }} />

          {/* Trend & Structure */}
          <Typography sx={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 1, px: 0.8, pt: 0.5, pb: 0.2 }}>
            Xu hướng
          </Typography>
          <IndGauge label="Khoảng cách MA20" value={ind.gap_ma20 != null ? ind.gap_ma20 * 100 : null} min={-15} max={15} thresholds={[-3, 1]} />
          <IndGauge label="Khoảng cách MA50" value={ind.gap_ma50 != null ? ind.gap_ma50 * 100 : null} min={-25} max={25} thresholds={[-5, 2]} />
          <IndGauge label="Độ dốc MA20" value={ind.ma20_slope != null ? ind.ma20_slope * 100 : null} min={-2} max={2} thresholds={[-0.1, 0.2]} />
          <IndGauge label="Sát hỗ trợ" value={ind.support_proximity} min={0} max={0.15} thresholds={[0.03, 0.1]} invert />
          <IndGauge label="Phục hồi" value={ind.recovery_score} min={-0.5} max={1} thresholds={[0, 0.3]} />
          <IndGauge label="Rút từ đỉnh" value={ind.dd_from_peak20 != null ? ind.dd_from_peak20 * 100 : null} min={-30} max={0} thresholds={[-10, -3]} />
        </Box>
      </DialogContent>
    </Dialog>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */
const UpsideMLDashboard = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Data
  const [dashboard, setDashboard] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [trend, setTrend] = useState([]);
  const [modelInfo, setModelInfo] = useState(null);
  const [heatmapData, setHeatmapData] = useState([]);

  // UX
  const [selectedDate, setSelectedDate] = useState('');
  const [availableDates, setAvailableDates] = useState([]);
  const [searchTicker, setSearchTicker] = useState('');
  const [filterTier, setFilterTier] = useState(3);
  const [sortConfig, setSortConfig] = useState({ key: 'ml_prob', dir: 'desc' });
  const [detailStock, setDetailStock] = useState(null);

  const [prevDashboard, setPrevDashboard] = useState(null);
  const [calculating, setCalculating] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  /* ─── Data fetching ──────────────────────────────────────── */
  const fetchData = useCallback(async (targetDate) => {
    setLoading(true);
    setError(null);
    try {
      const results = await Promise.allSettled([
        upsideMLAPI.getDashboard(targetDate),
        upsideMLAPI.getTiers(targetDate),
        upsideMLAPI.getTrend(30),
        upsideMLAPI.getModel(),
        upsideMLAPI.getHeatmap(targetDate),
        upsideMLAPI.getDates(30),
      ]);

      const [dashRes, tierRes, trendRes, modelRes, hmRes, datesRes] = results;
      if (dashRes.status === 'fulfilled') setDashboard(dashRes.value.data);
      if (tierRes.status === 'fulfilled') setTiers(tierRes.value.data);
      if (trendRes.status === 'fulfilled') setTrend(trendRes.value.data);
      if (modelRes.status === 'fulfilled') setModelInfo(modelRes.value.data);
      if (hmRes.status === 'fulfilled') setHeatmapData(hmRes.value.data);

      if (datesRes.status === 'fulfilled') {
        const dates = datesRes.value.data;
        setAvailableDates(dates);
        if (!targetDate && dates.length > 0) setSelectedDate(dates[0]);
      }

      if (datesRes.status === 'fulfilled' && datesRes.value.data.length >= 2) {
        const prevDate = datesRes.value.data[1];
        try {
          const prevRes = await upsideMLAPI.getDashboard(prevDate);
          setPrevDashboard(prevRes.data);
        } catch { setPrevDashboard(null); }
      }
    } catch (err) {
      setError(err.message || t('upsideML.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─ On-demand recalculate ─ */
  const handleRecalculate = useCallback(async () => {
    setCalculating(true);
    try {
      const res = await upsideMLAPI.calculate();
      const d = res.data;
      setSnackbar({
        open: true, severity: 'success',
        message: t('upsideML.recalculate.success', {
          n: d.n_saved, opportunities: d.n_opportunities, seconds: d.elapsed_seconds,
        }),
      });
      await fetchData();
    } catch (e) {
      setSnackbar({
        open: true, severity: 'error',
        message: t('upsideML.recalculate.error', { detail: e.response?.data?.detail || e.message }),
      });
    } finally {
      setCalculating(false);
    }
  }, [fetchData, t]);

  const handleDateChange = (e) => {
    const d = e.target.value;
    setSelectedDate(d);
    fetchData(d);
  };

  /* ─── Computed values ────────────────────────────────────── */
  const summary = dashboard?.summary;
  const strongBuys = useMemo(() => dashboard?.strong_buys || [], [dashboard?.strong_buys]);

  const regime = useMemo(() => {
    if (summary?.mkt_regime_risk_ema == null) return { label: t('upsideML.regime.calm'), color: '#16a34a', value: 0 };
    const r = summary.mkt_regime_risk_ema;
    if (r >= 0.40) return { label: t('upsideML.regime.highRisk'), color: '#dc2626', value: r * 100 };
    if (r >= 0.15) return { label: t('upsideML.regime.moderate'), color: '#ea580c', value: r * 100 };
    return { label: t('upsideML.regime.calm'), color: '#16a34a', value: r * 100 };
  }, [summary, t]);

  /* ─ Stale data detection ─ */
  const dataDate = summary?.date ? new Date(summary.date) : null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const daysDiff = dataDate ? Math.floor((today - dataDate) / (1000 * 60 * 60 * 24)) : 0;
  const isStale = daysDiff > 3;

  const pulseScore = useMemo(() => {
    if (!summary) return 0;
    const probScore = (summary.avg_prob || 0) * 100;
    const hotScore = Math.min((summary.n_hot || 0) * 5, 30);
    const warmScore = Math.min((summary.n_warm || 0) * 2, 20);
    return Math.min(probScore + hotScore + warmScore, 100);
  }, [summary]);

  const pulseInfo = useMemo(() => {
    if (pulseScore >= 60) return { label: t('upsideML.pulse.strong'), color: '#16a34a' };
    if (pulseScore >= 35) return { label: t('upsideML.pulse.moderate'), color: '#ea580c' };
    if (pulseScore >= 15) return { label: t('upsideML.pulse.weak'), color: '#9ca3af' };
    return { label: t('upsideML.pulse.quiet'), color: '#d1d5db' };
  }, [pulseScore, t]);

  const delta = useMemo(() => {
    if (!summary || !prevDashboard?.summary) return {};
    const prev = prevDashboard.summary;
    return {
      opportunities: summary.n_opportunities - prev.n_opportunities,
      avgProb: +((summary.avg_prob - prev.avg_prob) * 100).toFixed(1),
      strongBuys: strongBuys.length - (prevDashboard.strong_buys?.length || 0),
    };
  }, [summary, prevDashboard, strongBuys]);

  const trendChartData = useMemo(() => {
    return [...trend].reverse().map(p => ({
      date: p.date.slice(5),
      hot: p.n_hot,
      warm: p.n_warm,
      avgUpside: +(p.avg_prob * 100).toFixed(1),
      regime: p.mkt_regime ? +(p.mkt_regime * 100).toFixed(1) : null,
    }));
  }, [trend]);

  const forecast = useMemo(() => {
    if (trendChartData.length < 5) return null;
    const recent = trendChartData.slice(-7);
    const n = recent.length;
    const xMean = (n - 1) / 2;
    const yMean = recent.reduce((s, d) => s + d.avgUpside, 0) / n;
    let numr = 0, denr = 0;
    recent.forEach((d, i) => {
      numr += (i - xMean) * (d.avgUpside - yMean);
      denr += (i - xMean) ** 2;
    });
    const slope = denr !== 0 ? numr / denr : 0;
    const projected = [];
    for (let i = 1; i <= 3; i++) {
      projected.push({
        date: `+${i}d`,
        avgUpside: +Math.max(0, Math.min(100, yMean + slope * (n - 1 + i))).toFixed(1),
        _projected: true,
      });
    }

    // Market regime risk check — override bullish signals when regime is dangerous
    const regimeRisk = summary?.mkt_regime_risk_ema ?? 0;
    const isHighRisk = regimeRisk >= 0.40;
    const isModerateRisk = regimeRisk >= 0.15;

    let direction, dirColor;
    if (isHighRisk && slope > -0.2) {
      // Regime override: breadth may look OK but market regime is dangerous
      direction = t('upsideML.outlook.regimeWarning');
      dirColor = '#dc2626';
    } else if (isModerateRisk && slope > 0.2) {
      // Moderate risk: downgrade bullish direction
      direction = t('upsideML.outlook.regimeCaution');
      dirColor = '#ea580c';
    } else if (slope > 0.8) { direction = t('upsideML.outlook.risingFast'); dirColor = '#16a34a'; }
    else if (slope > 0.2) { direction = t('upsideML.outlook.rising'); dirColor = '#22c55e'; }
    else if (slope > -0.2) { direction = t('upsideML.outlook.stable'); dirColor = '#9ca3af'; }
    else if (slope > -0.8) { direction = t('upsideML.outlook.falling'); dirColor = '#ea580c'; }
    else { direction = t('upsideML.outlook.fallingFast'); dirColor = '#dc2626'; }

    let guidance;
    if (isHighRisk && slope > -0.2) {
      guidance = t('upsideML.outlook.guidance.regimeHighRisk');
    } else if (isModerateRisk && slope > 0.2) {
      guidance = t('upsideML.outlook.guidance.regimeModerate');
    } else if (slope > 0.8) guidance = t('upsideML.outlook.guidance.risingFast');
    else if (slope > 0.2) guidance = t('upsideML.outlook.guidance.rising');
    else if (slope > -0.2) guidance = t('upsideML.outlook.guidance.stable');
    else if (slope > -0.8) guidance = t('upsideML.outlook.guidance.falling');
    else guidance = t('upsideML.outlook.guidance.fallingFast');

    return { projected, direction, dirColor, slope, guidance, regimeOverride: isHighRisk || (isModerateRisk && slope > 0.2) };
  }, [trendChartData, t, summary]);

  const tierStats = useMemo(() => {
    if (!heatmapData.length) return {};
    const stats = {};
    [0, 1, 2, 3].forEach(tier => {
      const stocks = heatmapData.filter(s => s.opportunity_tier === tier);
      stats[tier] = {
        count: stocks.length,
        avgProb: stocks.length ? stocks.reduce((sum, st) => sum + st.ml_prob, 0) / stocks.length : 0,
        buySignals: stocks.filter(s => s.buy_signal).length,
      };
    });
    return stats;
  }, [heatmapData]);

  // Tier tab definitions
  const TIER_TABS = useMemo(() => [
    { key: 3, label: 'HOT',  icon: <FireIcon sx={{ fontSize: 16 }} />,   color: '#16a34a' },
    { key: 2, label: 'WARM', icon: <SunnyIcon sx={{ fontSize: 16 }} />,  color: '#ca8a04' },
    { key: 1, label: 'MILD', icon: <CloudIcon sx={{ fontSize: 16 }} />,  color: '#2563eb' },
  ], []);

  const filteredStocks = useMemo(() => {
    let list = heatmapData.filter(s => s.opportunity_tier === filterTier);
    if (searchTicker.trim()) {
      const q = searchTicker.trim().toUpperCase();
      list = list.filter(s => s.ticker.includes(q));
    }
    const { key, dir } = sortConfig;
    list.sort((a, b) => {
      let va = a[key], vb = b[key];
      if (typeof va === 'string') { va = va.toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (va < vb) return dir === 'asc' ? -1 : 1;
      if (va > vb) return dir === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [heatmapData, filterTier, searchTicker, sortConfig]);

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      dir: prev.key === key && prev.dir === 'desc' ? 'asc' : 'desc',
    }));
  };

  /* ═══════════════════════════════════════════════════════════
     RENDER
     ═══════════════════════════════════════════════════════════ */
  if (loading) {
    const skPulse = { animation: 'wave' };
    return (
      <Box sx={{ maxWidth: 1440, mx: 'auto', pb: 4 }}>
        {/* Header skeleton */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1.5, md: 3 }, pt: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Skeleton variant="rounded" width={42} height={42} sx={{ borderRadius: 1.5, flexShrink: 0 }} {...skPulse} />
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="text" width={180} height={30} {...skPulse} />
                <Skeleton variant="rounded" width={36} height={20} sx={{ borderRadius: 1 }} {...skPulse} />
              </Box>
              <Skeleton variant="text" width={260} height={16} {...skPulse} />
              <Skeleton variant="text" width={140} height={14} {...skPulse} />
            </Box>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Skeleton variant="rounded" width={130} height={34} sx={{ borderRadius: 1 }} {...skPulse} />
            <Skeleton variant="rounded" width={34} height={34} sx={{ borderRadius: 1 }} {...skPulse} />
          </Box>
        </Box>

        <Box sx={{ px: { xs: 1.5, md: 3 } }}>
          {/* ROW 1: 4 stat cards */}
          <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
            {[0, 1, 2, 3].map(i => (
              <Grid size={{ xs: 6, sm: 6, md: 3 }} key={i}>
                <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #f0f0f0' }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <Box sx={{ flex: 1 }}>
                      <Skeleton variant="text" width={80} height={14} sx={{ mb: 0.5 }} {...skPulse} />
                      <Skeleton variant="text" width={60} height={30} sx={{ mb: 0.3 }} {...skPulse} />
                      <Skeleton variant="text" width={110} height={13} {...skPulse} />
                    </Box>
                    <Skeleton variant="rounded" width={32} height={32} sx={{ borderRadius: 1, flexShrink: 0 }} {...skPulse} />
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>

          {/* ROW 2: Strong Buy strip */}
          <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #f0f0f0', mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
              <Skeleton variant="circular" width={18} height={18} {...skPulse} />
              <Skeleton variant="text" width={180} height={22} {...skPulse} />
              <Skeleton variant="rounded" width={52} height={20} sx={{ borderRadius: 1, ml: 'auto' }} {...skPulse} />
            </Box>
            <Grid container spacing={1.5}>
              {[0, 1, 2, 3].map(i => (
                <Grid size={{ xs: 6, sm: 4, md: 3 }} key={i}>
                  <Skeleton variant="rounded" width="100%" height={72} sx={{ borderRadius: 1.5 }} {...skPulse} />
                </Grid>
              ))}
            </Grid>
          </Paper>

          {/* ROW 3: Forecast + Trend chart */}
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
              <Paper elevation={0} sx={{ p: 2.5, borderRadius: 1.5, border: '1px solid #f0f0f0', flex: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 2 }}>
                  <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: 1 }} {...skPulse} />
                  <Skeleton variant="text" width={120} height={20} {...skPulse} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={64} sx={{ borderRadius: 1, mb: 2 }} {...skPulse} />
                <Grid container spacing={1} sx={{ mb: 2 }}>
                  {[0, 1, 2].map(i => (
                    <Grid size={4} key={i}>
                      <Skeleton variant="rounded" width="100%" height={48} sx={{ borderRadius: 1 }} {...skPulse} />
                    </Grid>
                  ))}
                </Grid>
                {[0, 1, 2, 3].map(i => (
                  <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                    <Skeleton variant="rounded" width={40} height={16} sx={{ borderRadius: 0.5 }} {...skPulse} />
                    <Skeleton variant="rounded" width="60%" height={8} sx={{ borderRadius: 0.5 }} {...skPulse} />
                    <Skeleton variant="text" width={24} height={14} sx={{ ml: 'auto' }} {...skPulse} />
                  </Box>
                ))}
              </Paper>
            </Grid>
            <Grid size={{ xs: 12, md: 8 }} sx={{ display: 'flex' }}>
              <Paper elevation={0} sx={{ p: 2, borderRadius: 1.5, border: '1px solid #f0f0f0', flex: 1, minHeight: 340 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <Skeleton variant="rounded" width={24} height={24} sx={{ borderRadius: 1 }} {...skPulse} />
                  <Skeleton variant="text" width={200} height={22} {...skPulse} />
                </Box>
                <Skeleton variant="rounded" width="100%" height={280} sx={{ borderRadius: 1 }} {...skPulse} />
              </Paper>
            </Grid>
          </Grid>

          {/* ROW 4: Table */}
          <Paper elevation={0} sx={{ borderRadius: 1.5, border: '1px solid #f0f0f0', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, py: 1.5, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Skeleton variant="text" width={140} height={22} {...skPulse} />
                <Skeleton variant="rounded" width={28} height={20} sx={{ borderRadius: 1 }} {...skPulse} />
              </Box>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                {[0, 1, 2, 3].map(i => (
                  <Skeleton key={i} variant="rounded" width={52} height={26} sx={{ borderRadius: 1 }} {...skPulse} />
                ))}
              </Box>
            </Box>
            <Divider />
            <Table size="small">
              <TableHead>
                <TableRow>
                  {['#', 'Mã', 'Xác Suất', 'Tier', 'Vị Thế', 'Tin Cậy', 'Tín Hiệu', 'Hành Động'].map((h, i) => (
                    <TableCell key={i} sx={{ py: 1, borderBottom: '1px solid #f0f0f0' }}>
                      <Skeleton variant="text" width={i === 0 ? 20 : i === 1 ? 36 : 55} height={16} {...skPulse} />
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {[...Array(8)].map((_, r) => (
                  <TableRow key={r}>
                    {[20, 48, 60, 44, 50, 44, 52, 60].map((w, c) => (
                      <TableCell key={c} sx={{ py: 1.2, borderBottom: '1px solid #f8f8f8' }}>
                        <Skeleton variant={c === 3 ? 'rounded' : 'text'} width={w} height={c === 3 ? 20 : 16}
                          sx={c === 3 ? { borderRadius: 1 } : {}} {...skPulse} />
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Paper>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Alert
          severity="error"
          sx={{ borderRadius: 1.5 }}
          action={
            <Chip label={t('upsideML.retry')} onClick={() => fetchData()} size="small"
              sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 600 }} />
          }
        >
          {error}
        </Alert>
      </Box>
    );
  }

  if (!dashboard) return null;

  return (
    <Box sx={{ maxWidth: 1440, mx: 'auto', pb: 4 }}>

      {/* ═══════════════════════════════════════════════════════
          HEADER — Consistent with other pages
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1.5, md: 3 }, pt: 1 }}>
        {/* Left: Icon + Title */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: 'linear-gradient(135deg, #16a34a 0%, #059669 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: '0 4px 14px rgba(22, 163, 74, 0.3)',
          }}>
            <TrendingUpIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {t('upsideML.pageTitle')}
              </Typography>
              {modelInfo && (
                <Tooltip title={t('upsideML.model.chipLabel', { version: modelInfo.version?.toUpperCase(), nModels: modelInfo.n_models })} arrow>
                  <Chip
                    label={modelInfo.version?.toUpperCase()}
                    size="small"
                    sx={{ height: 20, fontSize: 10, fontWeight: 700, bgcolor: '#f0fdf4', color: '#16a34a', border: '1px solid #bbf7d0' }}
                  />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('upsideML.pageDesc')}
              {modelInfo && ` · ${modelInfo.n_models} models · ${modelInfo.feature_count || ''} features`.replace('·  features', '')}
            </Typography>
            <Typography sx={{ fontSize: '0.7rem', color: '#b0b8c4', fontWeight: 400 }}>
              {selectedDate && `${selectedDate} · `}
              {summary ? `${summary.n_tickers} ${t('upsideML.intel.total', { n: '' }).trim()}` : ''}
            </Typography>
          </Box>
        </Box>

        {/* Right: Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControl size="small">
            <Select
              value={selectedDate}
              onChange={handleDateChange}
              displayEmpty
              sx={{
                fontSize: 13, minWidth: 130,
                '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e2e8f0' },
                '& .MuiSelect-select': { py: 0.7 },
              }}
            >
              {availableDates.map(d => (
                <MenuItem key={d} value={d} sx={{ fontSize: 13 }}>{d}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <Button
            variant="contained"
            startIcon={<CalculateIcon />}
            onClick={handleRecalculate}
            disabled={calculating}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              bgcolor: '#16a34a', '&:hover': { bgcolor: '#15803d' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {calculating ? t('upsideML.recalculate.running') : t('upsideML.recalculate.button')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => fetchData(selectedDate)}
            sx={{ textTransform: 'none', fontSize: 13, fontWeight: 600, borderRadius: '4px',
              color: '#64748b', borderColor: '#e2e8f0',
              '&:hover': { bgcolor: '#f8fafc', borderColor: '#cbd5e1' },
              '& .MuiButton-startIcon': { mr: 0.5 } }}
          >
            {t('upsideML.refresh')}
          </Button>
        </Box>
      </Box>

      {/* ── Calculating Banner ── */}
      {calculating && (
        <Alert
          severity="info"
          sx={{ mx: { xs: 1.5, md: 3 }, mb: 2, borderRadius: 2, '& .MuiAlert-icon': { alignItems: 'center' } }}
          icon={<CircularProgress size={18} />}
        >
          <Typography sx={{ fontWeight: 600, fontSize: '0.85rem' }}>
            {t('upsideML.recalculate.banner')}
          </Typography>
        </Alert>
      )}

      {/* ── Stale Data Warning ── */}
      {summary && isStale && !calculating && (
        <Alert
          severity="info"
          variant="outlined"
          sx={{ mx: { xs: 1.5, md: 3 }, mb: 2, borderRadius: 2, '& .MuiAlert-message': { fontSize: 13, fontWeight: 500 } }}
        >
          {t('upsideML.staleDataWarning', { date: summary.date, days: daysDiff })}
        </Alert>
      )}

      {/* Market alert banner */}
      {summary && summary.n_hot >= 3 && (() => {
        const breadth = summary.breadth_ma20 ?? 0;
        const regimeEma = summary.mkt_regime_risk_ema ?? 0;
        const highRisk = regimeEma >= 0.40;
        const lowBreadth = breadth < 0.35;
        // Pick the right banner variant based on actual market conditions
        let bannerKey, bannerColor, bannerBg, bannerBorder;
        if (highRisk || lowBreadth) {
          bannerKey = 'cautionOpportunities';
          bannerColor = '#92400e'; bannerBg = '#fffbeb'; bannerBorder = '#fde68a';
        } else {
          bannerKey = 'manyOpportunities';
          bannerColor = '#15803d'; bannerBg = '#f0fdf4'; bannerBorder = '#bbf7d0';
        }
        return (
          <Fade in>
            <Box sx={{
              mx: { xs: 1.5, md: 3 }, mb: 2, px: 2, py: 1, borderRadius: 1,
              bgcolor: bannerBg, border: `1px solid ${bannerBorder}`,
              display: 'flex', alignItems: 'center', gap: 1,
            }}>
              <FireIcon sx={{ fontSize: 18, color: bannerColor }} />
              <Typography sx={{ fontSize: 12.5, fontWeight: 600, color: bannerColor }}>
                {t(`upsideML.marketBanner.${bannerKey}`, { hot: summary.n_hot })}
              </Typography>
            </Box>
          </Fade>
        );
      })()}

      {/* Content area */}
      <Box sx={{ px: { xs: 1.5, md: 3 } }}>

        {/* ═══════════════════════════════════════════════════════
            ROW 1: Pulse Gauge + Smart Stat Cards
            ═══════════════════════════════════════════════════════ */}
        <Grid container spacing={1.5} sx={{ mb: 2.5 }}>
          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <GlassCard
              title={t('upsideML.statCards.activeOpportunities')}
              value={summary?.n_opportunities || 0}
              sub={`${summary?.n_hot || 0} HOT · ${summary?.n_warm || 0} WARM · ${summary?.n_mild || 0} MILD`}
              color="#16a34a"
              icon={<FireIcon sx={{ fontSize: 14, color: '#16a34a' }} />}
              delta={delta.opportunities}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <GlassCard
              title={t('upsideML.statCards.avgUpside')}
              value={`${(summary?.avg_prob * 100).toFixed(1)}%`}
              sub={t('upsideML.statCards.maxUpside', { max: (summary?.max_prob * 100).toFixed(1) })}
              color="#2563eb"
              icon={<TrendingUpIcon sx={{ fontSize: 14, color: '#2563eb' }} />}
              delta={delta.avgProb}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <GlassCard
              title={t('upsideML.statCards.strongBuys')}
              value={strongBuys.length}
              sub={t('upsideML.statCards.strongBuysSub')}
              color="#ea580c"
              icon={<TrophyIcon sx={{ fontSize: 14, color: '#ea580c' }} />}
              delta={delta.strongBuys}
            />
          </Grid>

          <Grid size={{ xs: 6, sm: 6, md: 3 }}>
            <GlassCard
              title={t('upsideML.statCards.marketRegime')}
              value={regime.label}
              sub={t('upsideML.statCards.regimeSub', {
                regime: summary?.mkt_regime_risk_ema != null ? (summary.mkt_regime_risk_ema * 100).toFixed(0) : '0',
                breadth: summary?.breadth_ma20 != null ? (summary.breadth_ma20 * 100).toFixed(0) : '-',
              })}
              color={regime.color}
              icon={<SpeedIcon sx={{ fontSize: 14, color: regime.color }} />}
            />
          </Grid>
        </Grid>

        {/* ═══════════════════════════════════════════════════════
            ROW 2: Strong Buy Strip + Outlook Panel
            ═══════════════════════════════════════════════════════ */}
        {strongBuys.length > 0 && (
          <Box sx={{ mb: 3 }}>
            <Section
              title={t('upsideML.sections.strongBuyTitle', { count: strongBuys.length })}
              icon={<BoltIcon sx={{ fontSize: 18, color: '#16a34a' }} />}
              count={strongBuys.length}
              badge="ACTION"
              contentSx={{ p: 0 }}
            >
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{
                      background: 'linear-gradient(180deg, #f0fdf4 0%, #ecfdf5 100%)',
                      '& th': { fontWeight: 700, fontSize: 11, color: '#15803d', py: 1, borderBottom: '2px solid #bbf7d0', whiteSpace: 'nowrap' },
                    }}>
                      <TableCell>{t('upsideML.tableHeaders.ticker')}</TableCell>
                      <TableCell align="right">{t('upsideML.tableHeaders.upsideProb')}</TableCell>
                      <TableCell align="right">{t('upsideML.tableHeaders.position')}</TableCell>
                      <TableCell align="right">Close</TableCell>
                      <TableCell align="right">RSI</TableCell>
                      <TableCell align="right">Ret 5D</TableCell>
                      <TableCell align="center">{t('upsideML.tableHeaders.risk')}</TableCell>
                      <TableCell>{t('upsideML.tableHeaders.reasons', 'Lý do')}</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {strongBuys.slice(0, 8).map(stock => {
                      const prob = (stock.ml_prob * 100).toFixed(1);
                      const pos = (stock.target_position * 100).toFixed(0);
                      const hasRisk = stock.risk_conflict;
                      const riskColor = stock.risk_tier >= 3 ? '#dc2626' : stock.risk_tier >= 2 ? '#ea580c' : stock.risk_tier >= 1 ? '#eab308' : '#22c55e';
                      const riskLabel = stock.risk_tier >= 3 ? 'CRITICAL' : stock.risk_tier >= 2 ? 'SEVERE' : stock.risk_tier >= 1 ? 'CAUTION' : 'SAFE';
                      return (
                        <TableRow
                          key={stock.ticker}
                          hover
                          onClick={() => navigate(`/upside-detail/${stock.ticker}`)}
                          sx={{
                            cursor: 'pointer',
                            background: hasRisk
                              ? 'linear-gradient(90deg, #fff7ed 0%, #fff 30%)'
                              : 'linear-gradient(90deg, #f0fdf4 0%, #fff 30%)',
                            '& td': { borderBottom: '1px solid #dcfce7', py: 1.2 },
                            '& td:first-of-type': { borderLeft: `3px solid ${hasRisk ? '#ea580c' : '#16a34a'}` },
                            '&:hover': { bgcolor: '#ecfdf5' },
                          }}
                        >
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              <Typography sx={{ fontWeight: 800, fontSize: 14, letterSpacing: 0.3 }}>{stock.ticker}</Typography>
                              <Chip
                                icon={<BoltIcon sx={{ fontSize: 11 }} />}
                                label={t('upsideML.signals.strongBuy')}
                                size="small"
                                sx={{ bgcolor: '#16a34a', color: '#fff', fontWeight: 700, fontSize: 10, height: 20, '& .MuiChip-icon': { color: 'inherit' } }}
                              />
                              {hasRisk && (
                                <Chip
                                  icon={<WarningIcon sx={{ fontSize: 10 }} />}
                                  label={stock.risk_label}
                                  size="small"
                                  sx={{ bgcolor: RISK[stock.risk_tier]?.bg, color: '#fff', fontWeight: 700, fontSize: 9, height: 18, '& .MuiChip-icon': { color: 'inherit' } }}
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, justifyContent: 'flex-end' }}>
                              <Typography sx={{ fontWeight: 800, fontSize: 15, color: hasRisk ? '#ea580c' : '#16a34a' }}>{prob}%</Typography>
                              <Box sx={{ width: 40, height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: '#dcfce7' }}>
                                <Box sx={{ height: '100%', borderRadius: 2, width: `${Math.min(stock.ml_prob * 100, 100)}%`, bgcolor: hasRisk ? '#ea580c' : '#16a34a' }} />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 700, fontSize: 13 }}>{pos}%</Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 600, fontSize: 12, color: '#374151' }}>
                              {stock.close != null ? Number(stock.close).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.rsi14 != null ? (stock.indicators.rsi14 < 30 ? '#dc2626' : stock.indicators.rsi14 > 65 ? '#16a34a' : '#374151') : '#d1d5db' }}>
                              {stock.indicators?.rsi14 != null ? stock.indicators.rsi14.toFixed(0) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.ret5 != null ? (stock.indicators.ret5 > 0 ? '#16a34a' : '#dc2626') : '#d1d5db' }}>
                              {stock.indicators?.ret5 != null ? `${(stock.indicators.ret5 * 100).toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{ fontSize: 10.5, fontWeight: 800, color: riskColor }}>{riskLabel}</Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4 }}>
                              {(stock.reasons || []).slice(0, 3).map(r => (
                                <Chip key={r} label={t(`upsideML.reasons.${r}`, r)} size="small" sx={{
                                  height: 18, fontSize: 9.5, fontWeight: 600,
                                  bgcolor: 'rgba(22,163,74,0.08)', color: '#16a34a',
                                  '& .MuiChip-label': { px: 0.6 },
                                }} />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </Section>
          </Box>
        )}

        {/* ═══════════════════════════════════════════════════════
            ROW 3: Forecast + 30-Day Trend Chart (side by side)
            ═══════════════════════════════════════════════════════ */}
        {(forecast || trendChartData.length > 0) && (
          <Grid container spacing={2.5} sx={{ mb: 3 }}>
            {forecast && (
              <Grid size={{ xs: 12, md: 4 }} sx={{ display: 'flex' }}>
                <Paper elevation={0} sx={{
                  p: 2.5, borderRadius: 1.5, border: '1px solid #e5e7eb',
                  flex: 1, display: 'flex', flexDirection: 'column',
                  background: 'linear-gradient(180deg, #f9fafb 0%, #fff 100%)',
                }}>
                  {/* Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, mb: 1.5 }}>
                    <Box sx={{
                      width: 24, height: 24, borderRadius: 1, display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                      background: `linear-gradient(135deg, ${forecast.dirColor}20, ${forecast.dirColor}08)`,
                    }}>
                      {forecast.slope > 0.2 ? <TrendingUpIcon sx={{ fontSize: 14, color: forecast.dirColor }} /> :
                        forecast.slope < -0.2 ? <TrendingDownIcon sx={{ fontSize: 14, color: forecast.dirColor }} /> :
                          <TrendingFlatIcon sx={{ fontSize: 14, color: forecast.dirColor }} />}
                    </Box>
                    <Typography sx={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, color: '#6b7280' }}>
                      {t('upsideML.outlook.shortTitle')}
                    </Typography>
                  </Box>

                  {/* Direction chip + avg label */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                    <Chip
                      label={forecast.direction}
                      size="small"
                      sx={{
                        height: 24, fontSize: 11, fontWeight: 700,
                        background: `linear-gradient(135deg, ${forecast.dirColor}, ${forecast.dirColor}dd)`,
                        color: '#fff',
                      }}
                    />
                    <Typography sx={{ fontSize: 12, color: '#6b7280' }}>
                      {t('upsideML.outlook.avgLabel', {
                        current: trendChartData.length > 0 ? `${trendChartData[trendChartData.length - 1].avgUpside}%` : '-',
                        forecast: `${forecast.projected[2]?.avgUpside || '-'}%`,
                      })}
                    </Typography>
                  </Box>

                  <Typography sx={{ fontSize: 12, color: forecast.regimeOverride ? '#dc2626' : '#6b7280', lineHeight: 1.6, mb: 1.5, fontWeight: forecast.regimeOverride ? 600 : 400 }}>
                    {forecast.guidance}
                  </Typography>

                  {/* ── Mini Sparkline: recent 5 days + 3 projected ── */}
                  {trendChartData.length >= 5 && (
                    <Box sx={{ mb: 1.5 }}>
                      <ResponsiveContainer width="100%" height={64}>
                        <ComposedChart data={[
                          ...trendChartData.slice(-5).map(d => ({ ...d, _projected: false })),
                          ...forecast.projected.map(d => ({ ...d, hot: null, warm: null, _projected: true })),
                        ]} margin={{ top: 4, right: 4, bottom: 0, left: 4 }}>
                          <Line type="monotone" dataKey="avgUpside" stroke={forecast.dirColor} strokeWidth={2} dot={false} />
                          <Line
                            type="monotone"
                            dataKey={(d) => d._projected ? d.avgUpside : null}
                            stroke={forecast.dirColor}
                            strokeWidth={2}
                            strokeDasharray="4 3"
                            dot={{ r: 2, fill: forecast.dirColor }}
                            connectNulls
                          />
                          <Area type="monotone" dataKey="hot" fill="#16a34a" fillOpacity={0.15} stroke="none" />
                          <XAxis dataKey="date" hide />
                          <YAxis hide domain={['dataMin - 5', 'dataMax + 5']} />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  )}

                  {/* ── Key Metrics Row ── */}
                  <Box sx={{
                    display: 'flex', gap: 1, mb: 1.5,
                    '& > div': {
                      flex: 1, p: 1, borderRadius: 1, bgcolor: '#f9fafb', border: '1px solid #f3f4f6',
                      textAlign: 'center',
                    },
                  }}>
                    <Box>
                      <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', mb: 0.3 }}>
                        Pulse
                      </Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: pulseInfo.color }}>
                        {Math.round(pulseScore)}
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: pulseInfo.color, fontWeight: 600 }}>
                        {pulseInfo.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', mb: 0.3 }}>
                        Regime
                      </Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: regime.color }}>
                        {regime.value.toFixed(0)}%
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: regime.color, fontWeight: 600 }}>
                        {regime.label}
                      </Typography>
                    </Box>
                    <Box>
                      <Typography sx={{ fontSize: 9, fontWeight: 700, textTransform: 'uppercase', color: '#9ca3af', mb: 0.3 }}>
                        Strong Buy
                      </Typography>
                      <Typography sx={{ fontSize: 16, fontWeight: 800, color: strongBuys.length > 0 ? '#ea580c' : '#9ca3af' }}>
                        {strongBuys.length}
                      </Typography>
                      <Typography sx={{ fontSize: 9, color: '#6b7280', fontWeight: 600 }}>
                        {t('upsideML.statCards.strongBuysSub')}
                      </Typography>
                    </Box>
                  </Box>

                  {/* ── Tier Distribution ── */}
                  <Divider sx={{ mb: 1.5, borderColor: '#f3f4f6' }} />
                  <Typography sx={{ fontSize: 10, fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.8, color: '#9ca3af', mb: 1 }}>
                    {t('upsideML.sections.tierDistribution')}
                  </Typography>

                  {[3, 2, 1, 0].map(tierVal => {
                    const tierData = tiers.find(t2 => t2.tier === tierVal);
                    const style = TIER[tierVal];
                    const count = tierData?.count || 0;
                    const pct = tierData?.pct || 0;
                    const maxCount = Math.max(...tiers.map(t2 => t2.count), 1);
                    const avg = tierStats[tierVal]?.avgProb;
                    const nBuys = tierStats[tierVal]?.buySignals || 0;
                    return (
                      <Box key={tierVal} sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: 0.7 }}>
                        <Box sx={{
                          width: 6, height: 6, borderRadius: '50%',
                          background: style.gradient, flexShrink: 0,
                        }} />
                        <Typography sx={{ fontSize: 11, fontWeight: 700, minWidth: 36, color: style.text }}>
                          {style.label}
                        </Typography>
                        <Typography sx={{ fontSize: 13, fontWeight: 800, minWidth: 22, textAlign: 'right' }}>
                          {count}
                        </Typography>
                        <Box sx={{
                          flex: 1, height: 5, borderRadius: 3, overflow: 'hidden',
                          bgcolor: '#f3f4f6',
                        }}>
                          <Box sx={{
                            height: '100%', borderRadius: 3,
                            width: `${maxCount > 0 ? (count / maxCount) * 100 : 0}%`,
                            background: style.gradient,
                            transition: 'width 0.6s ease-out',
                          }} />
                        </Box>
                        <Typography sx={{ fontSize: 10, minWidth: 24, textAlign: 'right', color: '#9ca3af' }}>
                          {pct}%
                        </Typography>
                        {avg != null && (
                          <Typography sx={{ fontSize: 10, minWidth: 28, textAlign: 'right', color: style.text, fontWeight: 600 }}>
                            ~{(avg * 100).toFixed(0)}%
                          </Typography>
                        )}
                        {nBuys > 0 && (
                          <Badge badgeContent={nBuys} color="success"
                            sx={{ '& .MuiBadge-badge': { fontSize: 9, height: 14, minWidth: 14, right: -2, top: -2 } }}>
                            <BoltIcon sx={{ fontSize: 11, color: '#16a34a' }} />
                          </Badge>
                        )}
                      </Box>
                    );
                  })}

                  <Typography sx={{ mt: 1, fontSize: 10.5, color: '#9ca3af' }}>
                    {t('upsideML.intel.total', { n: summary?.n_tickers || 0 })}
                    {' · '}
                    {t('upsideML.intel.passRate', { pct: summary?.n_tickers ? ((summary.n_opportunities / summary.n_tickers) * 100).toFixed(0) : '0' })}
                  </Typography>
                </Paper>
              </Grid>
            )}

            <Grid size={{ xs: 12, md: forecast ? 8 : 12 }} sx={{ display: 'flex' }}>
              <Paper elevation={0} sx={{
                flex: 1, borderRadius: 1.5, border: '1px solid #e5e7eb',
                display: 'flex', flexDirection: 'column', overflow: 'hidden',
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 2.5, pt: 2, pb: 1.5 }}>
                  <ChartIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
                  <Typography sx={{ fontWeight: 700, fontSize: 15 }}>
                    {t('upsideML.sections.upsideTrend')}
                  </Typography>
                </Box>
                {trendChartData.length > 0 ? (
                  <Box sx={{ position: 'relative', flex: 1, minHeight: 300, px: 0, pb: 2 }}>
                    <Box sx={{ position: 'absolute', inset: '0 0 16px 0' }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={[...trendChartData, ...(forecast?.projected || [])]} margin={{ top: 5, right: 8, bottom: 5, left: -10 }}>                          <defs>
                            <linearGradient id="upsideHotGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#16a34a" stopOpacity={0.35} />
                              <stop offset="100%" stopColor="#16a34a" stopOpacity={0.02} />
                            </linearGradient>
                            <linearGradient id="upsideWarmGrad" x1="0" y1="0" x2="0" y2="1">
                              <stop offset="0%" stopColor="#eab308" stopOpacity={0.25} />
                              <stop offset="100%" stopColor="#eab308" stopOpacity={0.02} />
                            </linearGradient>
                          </defs>
                          <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                          <XAxis dataKey="date" fontSize={10} tickLine={false} stroke="#d1d5db" />
                          <YAxis yAxisId="left" fontSize={10} tickLine={false} axisLine={false} stroke="#d1d5db" />
                          <YAxis yAxisId="right" orientation="right" fontSize={10} tickLine={false} axisLine={false} domain={[0, 100]} stroke="#d1d5db" />
                          <RTooltip
                            contentStyle={{
                              borderRadius: 1, border: '1px solid #e5e7eb',
                              fontSize: 12, boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
                              backdropFilter: 'blur(8px)',
                            }}
                          />
                          <Legend wrapperStyle={{ fontSize: 11, color: '#6b7280' }} />
                          <ReferenceLine yAxisId="right" y={20} stroke="#e5e7eb" strokeDasharray="5 5" />
                          <ReferenceLine yAxisId="right" y={50} stroke="#16a34a" strokeDasharray="5 5" strokeOpacity={0.3} />
                          <Area yAxisId="left" dataKey="hot" name={t('upsideML.chart.hot')} fill="url(#upsideHotGrad)" stroke="#16a34a" strokeWidth={2} />
                          <Area yAxisId="left" dataKey="warm" name={t('upsideML.chart.warm')} fill="url(#upsideWarmGrad)" stroke="#eab308" strokeWidth={1.5} />
                          <Line yAxisId="right" dataKey="avgUpside" name={t('upsideML.chart.avgUpsidePct')} stroke="#2563eb" strokeWidth={2.5} dot={false} />
                          <Line yAxisId="right" dataKey="regime" name={t('upsideML.chart.regimePct')} stroke="#9ca3af" strokeWidth={1} dot={false} strokeDasharray="4 4" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </Box>
                  </Box>
                ) : (
                  <Box sx={{ py: 5, textAlign: 'center', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography sx={{ color: '#d1d5db' }}>{t('upsideML.noTrendData')}</Typography>
                  </Box>
                )}
              </Paper>
            </Grid>
          </Grid>
        )}

        {/* ═══════════════════════════════════════════════════════
            ROW 4: Detailed Table (primary view)
            ═══════════════════════════════════════════════════════ */}
        <Box sx={{ mb: 3 }}>
          <Paper
            elevation={0}
            sx={{
              borderRadius: 1.5,
              border: '1px solid #e5e7eb',
              overflow: 'hidden',
            }}
          >
            {/* Tabs + Search row */}
            <Box sx={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              borderBottom: '2px solid #e5e7eb',
              px: { xs: 1, sm: 2 },
              background: 'linear-gradient(135deg, #fafafa 0%, #fff 100%)',
            }}>
              <Tabs
                value={filterTier}
                onChange={(_, v) => setFilterTier(v)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                  minHeight: 44,
                  '& .MuiTabs-indicator': {
                    height: 3, borderRadius: '2px 2px 0 0',
                    bgcolor: TIER_TABS.find(t => t.key === filterTier)?.color || '#374151',
                  },
                  '& .MuiTab-root': {
                    minHeight: 44, textTransform: 'none', fontWeight: 700,
                    fontSize: 13, px: 2, minWidth: 'auto',
                    color: '#9ca3af',
                    '&.Mui-selected': {
                      color: TIER_TABS.find(t => t.key === filterTier)?.color || '#374151',
                    },
                  },
                }}
              >
                {TIER_TABS.map(tab => {
                  const cnt = heatmapData.filter(s => s.opportunity_tier === tab.key).length;
                  if (cnt === 0) return null;
                  return (
                    <Tab
                      key={tab.key}
                      value={tab.key}
                      icon={tab.icon}
                      iconPosition="start"
                      label={`${tab.label} (${cnt})`}
                    />
                  );
                })}
              </Tabs>

              <TextField
                size="small"
                placeholder={t('upsideML.goToTicker')}
                value={searchTicker}
                onChange={e => setSearchTicker(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && searchTicker.trim() && filteredStocks.length === 0) {
                    navigate(`/upside-detail/${searchTicker.trim().toUpperCase()}`);
                  }
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon sx={{ fontSize: 18, color: '#9ca3af' }} /></InputAdornment>,
                }}
                sx={{
                  width: { xs: 140, sm: 170 }, flexShrink: 0,
                  '& .MuiOutlinedInput-root': { borderRadius: 1, fontSize: 13, bgcolor: '#f9fafb' },
                  '& .MuiOutlinedInput-input': { py: 0.8 },
                  '& .MuiOutlinedInput-notchedOutline': { borderColor: '#e5e7eb' },
                }}
              />
            </Box>

            {/* Table */}
            <TableContainer sx={{ overflow: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{
                    background: 'linear-gradient(180deg, #f9fafb 0%, #f3f4f6 100%)',
                    '& th': { fontWeight: 700, fontSize: 11.5, color: '#6b7280', py: 1.2, borderBottom: '2px solid #e5e7eb' },
                  }}>
                    <TableCell sx={{ width: 36 }}>#</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortConfig.key === 'ticker'}
                        direction={sortConfig.key === 'ticker' ? sortConfig.dir : 'asc'}
                        onClick={() => handleSort('ticker')}
                      >
                        {t('upsideML.tableHeaders.ticker')}
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'ml_prob'}
                        direction={sortConfig.key === 'ml_prob' ? sortConfig.dir : 'desc'}
                        onClick={() => handleSort('ml_prob')}
                      >
                        <Tooltip title={t('upsideML.tooltips.upsidePct')} arrow placement="top">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                            {t('upsideML.tableHeaders.upsideProb')}
                            <InfoIcon sx={{ fontSize: 12, color: '#d1d5db' }} />
                          </Box>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">{t('upsideML.tableHeaders.tier')}</TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortConfig.key === 'target_position'}
                        direction={sortConfig.key === 'target_position' ? sortConfig.dir : 'desc'}
                        onClick={() => handleSort('target_position')}
                      >
                        <Tooltip title={t('upsideML.tooltips.position')} arrow placement="top">
                          <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.3 }}>
                            {t('upsideML.tableHeaders.position')}
                            <InfoIcon sx={{ fontSize: 12, color: '#d1d5db' }} />
                          </Box>
                        </Tooltip>
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center">{t('upsideML.tableHeaders.action')}</TableCell>
                    <TableCell align="center">{t('upsideML.tableHeaders.risk')}</TableCell>
                    <TableCell align="right">Close</TableCell>
                    <TableCell align="right">RSI</TableCell>
                    <TableCell align="right">Gap MA20</TableCell>
                    <TableCell align="right">MA20 Slope</TableCell>
                    <TableCell align="right">Ret 5D</TableCell>
                    <TableCell align="right">Ret 20D</TableCell>
                    <TableCell align="right">Vol Z</TableCell>
                    <TableCell align="right">BB Pos</TableCell>
                    <TableCell align="right">DD Peak</TableCell>
                    <TableCell align="right">RS vs MKT</TableCell>
                    <TableCell>{t('upsideML.tableHeaders.reasons', 'Lý do')}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredStocks.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={18} align="center" sx={{ py: 5, color: '#d1d5db' }}>
                        {searchTicker ? `"${searchTicker}" — ${t('upsideML.noData')}` : t('upsideML.noData')}
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStocks.map((stock, i) => {
                      const tier = stock.opportunity_tier || 0;
                      const tierStyle = TIER[tier];
                      const prob = (stock.ml_prob * 100).toFixed(1);
                      const pos = (stock.target_position * 100).toFixed(0);
                      const confidence = (stock.confidence_label || 'LOW').toLowerCase();
                      const confColor = confidence === 'high' ? '#16a34a' : confidence === 'medium' ? '#ea580c' : '#dc2626';

                      let actionLabel, actionColor;
                      if (stock.risk_tier >= 3) {
                        actionLabel = t('upsideML.actions.avoid');
                        actionColor = '#7f1d1d';
                      } else if (stock.risk_tier >= 2) {
                        actionLabel = t('upsideML.actions.conflictWarn');
                        actionColor = '#dc2626';
                      } else if (stock.buy_signal) {
                        actionLabel = t('upsideML.actions.strongBuy');
                        actionColor = '#16a34a';
                      } else if (tier >= 3) {
                        actionLabel = t('upsideML.actions.buy');
                        actionColor = '#22c55e';
                      } else if (tier >= 2) {
                        actionLabel = t('upsideML.actions.accumulate');
                        actionColor = '#ea580c';
                      } else if (tier >= 1) {
                        actionLabel = t('upsideML.actions.watch');
                        actionColor = '#2563eb';
                      } else {
                        actionLabel = t('upsideML.actions.skip');
                        actionColor = '#9ca3af';
                      }

                      let riskLabel, riskColor;
                      if (stock.risk_tier >= 3) {
                        riskLabel = 'CRITICAL'; riskColor = '#dc2626';
                      } else if (stock.risk_tier >= 2) {
                        riskLabel = 'SEVERE'; riskColor = '#ea580c';
                      } else if (stock.risk_tier >= 1) {
                        riskLabel = 'CAUTION'; riskColor = '#eab308';
                      } else if (stock.risk_tier != null) {
                        riskLabel = 'SAFE'; riskColor = '#22c55e';
                      }

                      return (
                        <TableRow
                          key={stock.ticker}
                          hover
                          onClick={() => setDetailStock(stock)}
                          sx={{
                            cursor: 'pointer',
                            transition: 'background 0.15s',
                            '&:hover': { bgcolor: `${tierStyle.bg}99` },
                            '& td': { borderBottom: '1px solid #f3f4f6' },
                            '&:last-child td': { borderBottom: 0 },
                          }}
                        >
                          <TableCell>
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>
                              {i + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8 }}>
                              {/* Tier color dot */}
                              <Box sx={{
                                width: 4, height: 28, borderRadius: 1,
                                background: tierStyle.gradient, flexShrink: 0,
                              }} />
                              <Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                  <Typography sx={{ fontWeight: 700, fontSize: 14, letterSpacing: 0.2 }}>
                                    {stock.ticker}
                                  </Typography>
                                  {stock.buy_signal && (
                                    <BoltIcon sx={{ fontSize: 14, color: '#16a34a' }} />
                                  )}
                                </Box>
                                {/* Confidence indicator */}
                                <Tooltip title={t('upsideML.confidenceTooltip', { level: t(`upsideML.confidenceLevels.${confidence}`) })} arrow placement="right">
                                  <Chip
                                    size="small"
                                    label={t(`upsideML.confidenceLevels.${confidence}`)}
                                    sx={{
                                      height: 16, fontSize: 9, fontWeight: 700,
                                      bgcolor: `${confColor}14`, color: confColor,
                                      '& .MuiChip-label': { px: 0.5 },
                                    }}
                                  />
                                </Tooltip>
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, justifyContent: 'flex-end' }}>
                              <Typography sx={{ fontWeight: 700, fontSize: 14, color: tierStyle.text }}>
                                {prob}%
                              </Typography>
                              <Box sx={{
                                width: 48, height: 5, borderRadius: 3, overflow: 'hidden',
                                bgcolor: '#f3f4f6',
                              }}>
                                <Box sx={{
                                  height: '100%', borderRadius: 3,
                                  width: `${Math.min(stock.ml_prob * 100, 100)}%`,
                                  background: tierStyle.gradient,
                                  transition: 'width 0.5s ease-out',
                                }} />
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{
                              fontSize: 11, fontWeight: 800, letterSpacing: 0.5,
                              color: tierStyle.text,
                            }}>
                              {tierStyle.label}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontWeight: 600, fontSize: 13 }}>{pos}%</Typography>
                          </TableCell>
                          <TableCell align="center">
                            <Typography sx={{
                              fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
                              color: actionColor,
                            }}>
                              {actionLabel}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            {stock.risk_tier != null ? (
                              <Typography sx={{
                                fontSize: 11, fontWeight: 800, letterSpacing: 0.3,
                                color: riskColor,
                              }}>
                                {riskLabel}
                              </Typography>
                            ) : (
                              <Typography sx={{ color: '#d1d5db', fontSize: 12 }}>—</Typography>
                            )}
                          </TableCell>
                          {/* Indicators — all columns */}
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#374151' }}>
                              {stock.close != null ? Number(stock.close).toLocaleString() : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.rsi14 != null ? (stock.indicators.rsi14 < 30 ? '#dc2626' : stock.indicators.rsi14 > 65 ? '#16a34a' : '#374151') : '#d1d5db' }}>
                              {stock.indicators?.rsi14 != null ? stock.indicators.rsi14.toFixed(0) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.gap_ma20 != null ? (stock.indicators.gap_ma20 > 0 ? '#16a34a' : '#dc2626') : '#d1d5db' }}>
                              {stock.indicators?.gap_ma20 != null ? `${(stock.indicators.gap_ma20 * 100).toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.ma20_slope != null ? (stock.indicators.ma20_slope > 0.002 ? '#16a34a' : stock.indicators.ma20_slope < -0.002 ? '#dc2626' : '#374151') : '#d1d5db' }}>
                              {stock.indicators?.ma20_slope != null ? `${(stock.indicators.ma20_slope * 100).toFixed(2)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.ret5 != null ? (stock.indicators.ret5 > 0 ? '#16a34a' : '#dc2626') : '#d1d5db' }}>
                              {stock.indicators?.ret5 != null ? `${(stock.indicators.ret5 * 100).toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.ret20 != null ? (stock.indicators.ret20 > 0 ? '#16a34a' : '#dc2626') : '#d1d5db' }}>
                              {stock.indicators?.ret20 != null ? `${(stock.indicators.ret20 * 100).toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.vol_z != null ? (stock.indicators.vol_z > 1.5 ? '#16a34a' : stock.indicators.vol_z > 0.8 ? '#ea580c' : '#374151') : '#d1d5db' }}>
                              {stock.indicators?.vol_z != null ? stock.indicators.vol_z.toFixed(1) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.bb_pos != null ? (stock.indicators.bb_pos < 0.2 ? '#dc2626' : stock.indicators.bb_pos > 0.8 ? '#16a34a' : '#374151') : '#d1d5db' }}>
                              {stock.indicators?.bb_pos != null ? stock.indicators.bb_pos.toFixed(2) : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.dd_from_peak20 != null ? (stock.indicators.dd_from_peak20 < -0.1 ? '#dc2626' : stock.indicators.dd_from_peak20 < -0.05 ? '#ea580c' : '#374151') : '#d1d5db' }}>
                              {stock.indicators?.dd_from_peak20 != null ? `${(stock.indicators.dd_from_peak20 * 100).toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography sx={{ fontSize: 12, fontWeight: 600, color: stock.indicators?.rs_vs_mkt_5 != null ? (stock.indicators.rs_vs_mkt_5 > 0.03 ? '#16a34a' : stock.indicators.rs_vs_mkt_5 < -0.03 ? '#dc2626' : '#374151') : '#d1d5db' }}>
                              {stock.indicators?.rs_vs_mkt_5 != null ? `${(stock.indicators.rs_vs_mkt_5 * 100).toFixed(1)}%` : '—'}
                            </Typography>
                          </TableCell>
                          {/* Reasons column — last */}
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.4, maxWidth: 200 }}>
                              {(stock.reasons || []).slice(0, 3).map(r => (
                                <Chip key={r} label={t(`upsideML.reasons.${r}`, r)} size="small" sx={{
                                  height: 18, fontSize: 9.5, fontWeight: 600,
                                  bgcolor: tier >= 3 ? 'rgba(22,163,74,0.08)' : tier >= 2 ? 'rgba(202,138,4,0.08)' : 'rgba(37,99,235,0.08)',
                                  color: tier >= 3 ? '#16a34a' : tier >= 2 ? '#b45309' : '#2563eb',
                                  '& .MuiChip-label': { px: 0.6 },
                                }} />
                              ))}
                            </Box>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        </Box>
      </Box>

      {/* Stock detail dialog */}
      <StockDetailDialog
        stock={detailStock}
        open={!!detailStock}
        onClose={() => setDetailStock(null)}
        t={t}
      />

      {/* ═══ SNACKBAR ═══ */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar(s => ({ ...s, open: false }))}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%', borderRadius: 2 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default UpsideMLDashboard;
